import {
  CLUB_COLORS,
  CREST_ICONS,
  CREST_ICON_NAMES,
  DEFAULT_CREST,
  clubColorName,
  clubHex,
  type Crest,
} from '@/social/crewLogic';

/**
 * crestArt.ts: the RACER CLUB crest, drawn as a STRUCK METAL BADGE (M142
 * owner round 2: "make the club crests look a bit more realistic"; the owner
 * picked the enamel-pin / car-badge direction). The icon REPLACES the old
 * initials, so there is no text anywhere in the art.
 *
 * A football-club shield (viewBox 0 0 100 116, the M140 proportions every
 * crest CSS rule is sized against), photographed under one studio light from
 * the top-left:
 *
 *  - THE RIM is polished metal in the secondary colour with a real two-facet
 *    bevel. Every facet is shaded from its own direction through a tiny
 *    reflection model (envBrightness): a key softbox up-left, a bright
 *    ceiling, a dark horizon band, a lighter floor bounce. So the top edges
 *    catch the light, the inner facet runs the other way and the facets meet
 *    in mitres. Over that, the chrome HORIZON: both sides of the outer facet
 *    break from a bright sky reflection into a dark band at the same height
 *    (the inner facet breaks a little lower, like a real bevel), and the
 *    lit top-left shoulder carries one small specular hot spot.
 *  - THE FIELD is glossy hard enamel in the primary colour, set below the rim:
 *    the rim casts a soft inner shadow onto it, a fine sunburst guilloche
 *    shows through its middle band, and the glass carries a faint reflection
 *    with one crisp narrow streak on its edge.
 *  - THE ICON is raised struck metal: a lit chamfer on its top-left edges, a
 *    darker SIDE WALL down-right where the metal's thickness shows, a face
 *    with the same chrome horizon (arced on round reliefs, soft on flat
 *    ones), a tight contact shadow plus a soft cast shadow on the enamel, and
 *    its details either ENGRAVED (grooves) or RECESSED and filled with enamel
 *    (or a polished silver inlay when enamel would not show).
 *
 * Only gradients, clip paths, a mask and blur/offset filters are used (no SVG
 * lighting filters): WebKit renders those at a low resolution and they vary
 * between engines. Detail that would be noise at 40 px is low contrast, so
 * it fades into the tone instead of into speckle.
 *
 * Players may pick ANY pair from the 24 club colours, the same colour twice
 * included, so every tone is derived from the pair (crestTones): coloured
 * metals keep their own hue in every reflection (gold glints pale gold, not
 * cream), near-black metals become cool BLACK CHROME (a near-black body with
 * near-white streaks, never tinted by the field), a light icon on the same
 * light enamel steps its face one tone down so it stands off the field, and
 * a keyline only takes over (ink or a lit edge in the metal's own tone) when
 * the icon and field are too close to separate on their own.
 *
 * Each icon is drawn in its own 48x48 box (with a per-icon scale/offset so
 * every icon has a similar optical size and centre). Its silhouette is
 * defined ONCE per SVG and re-drawn through <use> for every pass (shadows,
 * keyline, side wall, chamfer, face), so the passes always hug the exact
 * silhouette and the markup stays small.
 *
 * Every SVG gets its own id prefix: gradient/clip/filter ids are
 * document-global, and a gradient defined inside a hidden element stops
 * painting for every other copy that points at it.
 */

let seq = 0;

// ---- colour maths -------------------------------------------------------------

type Rgb = [number, number, number];

function toRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/./g, '$&$&') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(c: Rgb): string {
  return (
    '#' +
    c
      .map((v) =>
        Math.round(Math.min(255, Math.max(0, v)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/** a to b by t (0 = a, 1 = b), in sRGB (plenty for UI tints). */
function mix(a: string, b: string, t: number): string {
  const x = toRgb(a);
  const y = toRgb(b);
  return toHex([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
}

/** Multiply: a coloured metal reflecting itself (gold gets a deeper, richer
 *  amber, silver a cool steel), which is how coloured metal darkens. */
function mul(a: string, b: string): string {
  const x = toRgb(a);
  const y = toRgb(b);
  return toHex([(x[0] * y[0]) / 255, (x[1] * y[1]) / 255, (x[2] * y[2]) / 255]);
}

/** WCAG relative luminance (0 black ... 1 white). */
function luminance(hex: string): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = toRgb(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** HSV saturation (0 grey ... 1 pure colour). */
function saturation(hex: string): number {
  const c = toRgb(hex);
  const hi = Math.max(...c);
  return hi === 0 ? 0 : (hi - Math.min(...c)) / hi;
}

/** The colour at full brightness (its hue, as light as it goes). */
function brighten(hex: string): string {
  const c = toRgb(hex);
  const k = 255 / Math.max(1, ...c);
  return toHex([c[0] * k, c[1] * k, c[2] * k]);
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const WHITE = '#ffffff';
const BLACK = '#000000';
/** The studio's light, a touch cool (what black chrome and steel reflect). */
const COOL = '#e8eef7';

/** The picker tile's backdrop when the caller gives none (STEALTH, the same
 *  colour as the tile CSS fallback). */
const DEFAULT_BACKDROP = '#2a2e37';

/** Below this icon/field contrast the KEYLINE is what separates the icon, so
 *  it is chosen against the field (and drawn heavier). */
const OUTLINE_PAIR = 3;
/** Below this contrast the pair is "the same colour": the enamel sits a touch
 *  deeper (dark pairs) or the icon's face a tone darker (light pairs). */
const SAMEISH_PAIR = 1.9;
/** A recessed detail must reach this contrast against the icon's face and
 *  highlights to keep the enamel-filled look; else it takes a polar tone. */
const CUT_MIN = 2.2;
/** Near-black metal (NAVY, STEALTH, BLACK, GRAPE): lighter enamel recesses. */
const DARK_ICON = 0.07;

/** Is a colour "light" for outline purposes? */
function isLight(hex: string): boolean {
  return luminance(hex) > 0.3;
}

// ---- the metal ----------------------------------------------------------------

/** A polished metal's reflection ramp, darkest to brightest, and how far it
 *  is black chrome (0 a coloured / light metal ... 1 black chrome). */
interface Ramp {
  deep: string;
  shade: string;
  base: string;
  hi: string;
  spec: string;
  dk: number;
}

/**
 * The ramp for metal `m`. A coloured metal reflects in its own hue: its
 * darks are a deep, saturated take on it, its brightest glint a pale tint of
 * it (pale gold, not cream; a neutral silver or white still reaches white).
 * A near-black metal becomes BLACK CHROME: a near-black body whose
 * reflections are cool steel (a touch of its own hue for NAVY or GRAPE),
 * never tinted by the field it sits on.
 */
function metalRamp(m: string): Ramp {
  const L = luminance(m);
  const s = saturation(m);
  const dk = clamp((0.11 - L) / 0.08, 0, 1);
  const mm = mul(m, m);
  const lit = {
    deep: mix(mm, BLACK, 0.5 + 0.2 * L),
    // lighter metals need a deeper shade to read as polished (white chrome)
    shade: mix(mix(m, mm, 0.55), BLACK, 0.12 + 0.22 * L),
    base: m,
    hi: mix(m, WHITE, 0.3 + 0.18 * (1 - s)),
    spec: mix(m, WHITE, 0.86 - 0.32 * s),
  };
  if (dk === 0) return { ...lit, dk };
  const light = mix(COOL, brighten(m), 0.3 * s);
  const blk = {
    deep: mix(m, BLACK, 0.62),
    shade: mix(m, BLACK, 0.2),
    base: mix(m, light, 0.13),
    hi: mix(m, light, 0.5),
    spec: mix(m, light, 0.92),
  };
  return {
    deep: mix(lit.deep, blk.deep, dk),
    shade: mix(lit.shade, blk.shade, dk),
    base: mix(lit.base, blk.base, dk),
    hi: mix(lit.hi, blk.hi, dk),
    spec: mix(lit.spec, blk.spec, dk),
    dk,
  };
}

/** A colour on the ramp: 0 deep, .25 shade, .5 base, .75 hi, 1 spec. Black
 *  chrome bends the curve down, so only the brightest reflections light up
 *  and the body stays near-black. */
function rampAt(r: Ramp, b: number): string {
  const stops = [r.deep, r.shade, r.base, r.hi, r.spec];
  const x = Math.pow(clamp(b, 0, 1), 1 + 0.7 * r.dk) * 4;
  const i = Math.min(3, Math.floor(x));
  return mix(stops[i], stops[i + 1], x - i);
}

/**
 * How bright a polished facet looks (0 ... 1, onto the ramp). The facet faces
 * (fx, fy) in the picture plane (y down) and leans `tilt` radians off facing
 * the viewer; its normal reflects the view into a studio: a key softbox up-
 * left, a bright ceiling, a dark horizon band just below sideways, a lighter
 * floor bounce, a touch more light on the left.
 */
const KEY: [number, number, number] = (() => {
  const v: [number, number, number] = [-0.5, -0.8, 0.34];
  const n = Math.hypot(...v);
  return [v[0] / n, v[1] / n, v[2] / n];
})();
function envBrightness(fx: number, fy: number, tilt: number): number {
  const s = Math.sin(2 * tilt);
  const c = Math.cos(2 * tilt);
  const rx = s * fx;
  const ry = s * fy;
  const rz = c;
  let b = 0.44 - 0.32 * ry - 0.12 * rx;
  const hz = (ry - 0.2) / 0.085;
  b -= 0.34 * Math.exp(-0.5 * hz * hz);
  b += 0.2 * clamp((ry - 0.45) / 0.4, 0, 1);
  const k = Math.max(0, rx * KEY[0] + ry * KEY[1] + rz * KEY[2]);
  b += 0.55 * Math.pow(k, 8);
  return clamp(b, 0, 1);
}

/**
 * The ramp moved `s` stops along itself (+1: every stop takes the next
 * brighter one's tone, -1: the next darker one's; fractions blend, and past
 * the ends it runs on toward black / white). How a face that would sit at
 * the enamel's own value is lifted (dark pairs) or pressed (light pairs).
 */
function shiftRamp(r: Ramp, s: number): Ramp {
  if (s === 0) return r;
  const stops = [r.deep, r.shade, r.base, r.hi, r.spec];
  const at = (x: number) => {
    if (x <= 0) return mix(stops[0], BLACK, Math.min(1, -x * 0.45));
    if (x >= 4) return mix(stops[4], WHITE, Math.min(1, (x - 4) * 0.5));
    const i = Math.floor(x);
    return mix(stops[i], stops[i + 1], x - i);
  };
  return {
    deep: at(s),
    shade: at(1 + s),
    base: at(2 + s),
    hi: at(3 + s),
    spec: at(4 + s),
    dk: r.dk,
  };
}

// ---- tones --------------------------------------------------------------------

/**
 * The field's enamel, top to bottom: deep and saturated, deeper toward the
 * bottom. A same-colour pair sits deeper (dark) so the polished rim and icon
 * lift off it; a light enamel (white, sunburst, gold) stays bright, or it
 * reads as grey metal (a light icon on it presses its own face down instead).
 */
function enamel(field: string, metal: string): { top: string; mid: string; low: string } {
  const lift = clamp((luminance(field) - 0.3) / 0.6, 0, 1);
  const mm = mul(field, field);
  if (contrast(metal, field) < SAMEISH_PAIR) {
    return {
      top: mix(field, BLACK, 0.06 * (1 - lift)),
      mid: mix(mix(field, mm, 0.5 * (1 - lift)), BLACK, 0.2 * (1 - lift) + 0.03 * lift),
      low: mix(mix(field, mm, 1 - lift), BLACK, 0.6 * (1 - lift) + 0.16 * lift),
    };
  }
  return { top: mix(field, WHITE, 0.05), mid: field, low: mix(field, BLACK, 0.48 - 0.3 * lift) };
}

/** The value step the icon's face keeps from the enamel behind it (its mid
 *  tone against the enamel's): below it, the face is lifted or pressed. */
const FACE_STEP = 1.8;

/** Every tone an icon is painted with, derived from (icon colour, backdrop). */
export interface CrestTones {
  /** The icon colour itself (the face's mid tone). */
  solid: string;
  /** A lighter face tone (raised plates, ears) and the face's lowest shade. */
  top: string;
  bottom: string;
  /** Lighter + darker shades of the icon colour (facets, highlights). */
  hi: string;
  lo: string;
  /** Recessed, enamel-filled areas (pentagons, windows, eyes, checks). */
  cut: string;
  /** How a recess is filled: glossy ENAMEL (an inner shadow under its top-
   *  left wall + a soft gloss) or a polished SILVER inlay (when the metal and
   *  field are too close for enamel to show). */
  cutKind: 'enamel' | 'silver';
  /** The keyline round the raised silhouette: its colour, its width (icon
   *  units, centred on the edge) and its opacity. A faint hairline by
   *  default; heavier when it is what separates the icon from a close field. */
  edge: string;
  edgeW: number;
  edgeOp: number;
  /** The metal reflection ramp (the chamfer, the side wall, highlights). */
  spec: string;
  deep: string;
  shade: string;
  /** The face's chrome reflection, top to bottom: the sky, the sky by the
   *  horizon, the bright streak on it, the dark band under it, the ground
   *  and the floor bounce at the bottom. */
  sky0: string;
  sky1: string;
  streak: string;
  dark: string;
  ground: string;
  floor: string;
  /** The chamfer: its lit (top-left) tone and the side wall's (bottom-right). */
  bevHi: string;
  bevLo: string;
  /** The lit chamfer's width scale (thinner on a light-on-light pair, so the
   *  keyline outside it shows). */
  bev: number;
  /** The side wall: its face-side tone, its far-edge tone, and the thin lit
   *  lip where it meets the face. */
  wallNear: string;
  wallFar: string;
  lip: string;
}

/**
 * Tones for an icon in `icon` metal sitting on a `field` enamel. Exported
 * for the colour-pair sweep in the QA tooling; the screens never need it.
 */
export function crestTones(icon: string, field: string): CrestTones {
  const L = luminance(icon);
  const darkIcon = L < DARK_ICON;
  const metal = metalRamp(icon);
  const pair = contrast(icon, field);
  // A face at the enamel's own value reads as a keyline drawing, not raised
  // metal. So it keeps a FACE_STEP from the enamel behind it: on a light
  // enamel (WHITE on WHITE, SUNBURST on GOLD) it is pressed down to darker,
  // antique polished steel or gold; on a dark one (NAVY on NAVY, BLACK on
  // BLACK, FOREST on NAVY) it is lifted toward the same polished tone the rim
  // shows, so rim and icon read as one metal.
  const back0 = enamel(field, icon).mid;
  const down = isLight(field) || luminance(icon) < luminance(back0) * 0.8;
  let shiftBy = 0;
  for (let i = 1; i <= 16 && contrast(shiftRamp(metal, shiftBy).base, back0) < FACE_STEP; i++) {
    shiftBy = (down ? -0.1 : 0.1) * i;
  }
  const r = shiftRamp(metal, shiftBy);
  const lightSame = shiftBy < 0;
  // What sits right behind the icon: the enamel around the icon's middle.
  const back = mix(field, BLACK, 0.1);

  let hi = r.hi;
  const k = r.dk;
  // The face reflection: black chrome keeps a darker sky so its body stays
  // near-black, with the near-white streak on the horizon.
  const sky0 = mix(mix(r.base, r.hi, 0.5), r.base, k);
  const sky1 = mix(r.hi, mix(r.base, r.hi, 0.6), k);

  // The keyline: a faint ink hairline where the raised metal meets the
  // enamel; when the metal alone does not stand out from the field, whichever
  // pole (ink, or a lit edge in the metal's own tone) contrasts more with the
  // FIELD, drawn heavier.
  const ink = mix(icon, BLACK, isLight(icon) ? 0.8 : 0.86);
  const glow = mix(metal.hi, metal.spec, 0.35);
  let edge = ink;
  let edgeW = 1.1;
  let edgeOp = 0.5;
  if (pair < OUTLINE_PAIR) {
    const useInk = contrast(ink, back) >= contrast(glow, back);
    edge = useInk ? ink : glow;
    edgeW = useInk ? 3.2 : 2.2;
    edgeOp = useInk ? 0.9 : 0.75;
  }

  // Recessed details: enamel in the field colour (a darker or, in near-black
  // metal, a lighter take on it); when that does not stand out from the
  // face AND its highlights, the better polar tone (a light one is a
  // polished silver inlay).
  const score = (c: string) => Math.min(contrast(c, r.base), contrast(c, hi));
  const fieldCut = darkIcon
    ? mix(field, WHITE, 0.25)
    : mix(field, BLACK, isLight(field) ? 0.32 : 0.18);
  const darkCut = mix(r.base, BLACK, 0.66);
  const lightCut = mix(icon, WHITE, 0.7);
  let cut = fieldCut;
  let cutKind: CrestTones['cutKind'] = 'enamel';
  if (score(fieldCut) < CUT_MIN) {
    const light = score(lightCut) > score(darkCut);
    cut = light ? lightCut : darkCut;
    if (light) cutKind = 'silver';
  }
  // Last guard: a highlight drawn on a cut (or a cut on a highlight) must
  // never melt into it.
  if (contrast(hi, cut) < 2)
    hi = luminance(cut) > luminance(r.base) ? mix(r.deep, BLACK, 0.3) : mix(r.hi, WHITE, 0.6);

  return {
    solid: r.base,
    top: mix(r.base, r.hi, 0.55),
    bottom: r.shade,
    hi,
    lo: mix(r.deep, r.shade, 0.3),
    cut,
    cutKind,
    edge,
    edgeW,
    edgeOp,
    spec: r.spec,
    deep: r.deep,
    shade: r.shade,
    sky0,
    sky1,
    streak: r.spec,
    dark: r.deep,
    ground: r.shade,
    floor: mix(r.shade, r.base, 0.6),
    bevHi: metal.spec,
    bevLo: r.deep,
    bev: lightSame ? 0.6 : 1,
    wallNear: mix(r.deep, r.shade, 0.42),
    wallFar: mix(r.deep, BLACK, 0.22),
    lip: mix(r.shade, r.base, 0.45),
  };
}

// ---- small svg helpers --------------------------------------------------------

const r2 = (n: number) => Math.round(n * 100) / 100;

function pt(x: number, y: number): string {
  return `${r2(x)} ${r2(y)}`;
}

function poly(points: readonly (readonly [number, number])[]): string {
  return 'M' + points.map(([x, y]) => pt(x, y)).join(' L') + ' Z';
}

/** A regular polygon's vertices. `rot` in degrees (-90 = a vertex up). */
function ngon(cx: number, cy: number, r: number, n: number, rot: number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = ((rot + (i * 360) / n) * Math.PI) / 180;
    out.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return out;
}

/** A five-point star path. */
function star(cx: number, cy: number, r: number, inner = 0.42): string {
  const pts: [number, number][] = [];
  for (let i = 0; i < 10; i++) {
    const a = ((-90 + i * 36) * Math.PI) / 180;
    const rr = i % 2 === 0 ? r : r * inner;
    pts.push([cx + rr * Math.cos(a), cy + rr * Math.sin(a)]);
  }
  return poly(pts);
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) =>
    c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '&' ? '&amp;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/** A number without its leading zero (.35, not 0.35): gradients carry a
 *  lot of them. */
const num = (n: number) => String(n).replace(/^(-?)0\./, '$1.');

/** A gradient stop. */
function stop(at: number, color: string, opacity?: number): string {
  const o = opacity === undefined || opacity >= 1 ? '' : ` stop-opacity="${num(r2(opacity))}"`;
  return `<stop offset="${num(at)}" stop-color="${color}"${o}/>`;
}

/** A detail path (filled). */
function dPath(d: string, fill: string, extra = ''): string {
  return `<path d="${d}" fill="${fill}"${extra}/>`;
}

/** A detail line (stroked). */
function dLine(d: string, stroke: string, w: number, extra = ''): string {
  return `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${r2(w)}"${extra}/>`;
}

/** The chamfer offset of recessed and engraved details (icon units). */
const RO = 0.4;

/** The SVG being drawn: its id prefix + a counter, so a long detail shape is
 *  defined once and painted three times through <use>. Rendering is
 *  synchronous, so one module-level slot is enough. */
const ctx = { id: '', n: 0 };
const uid = () => `${ctx.id}u${ctx.n++}`;
/** The recess fill of the SVG being drawn (a per-shape enamel/silver
 *  gradient, defined in iconDefs). */
const cutFill = () => `url(#${ctx.id}ce)`;

/** Paint one shape several times: inline when short, else defined once. */
function multi(d: string, def: string, paints: string[]): string {
  if (d.length < 64) return paints.map((p) => `<path d="${d}"${def}${p}/>`).join('');
  const u = uid();
  return (
    `<defs><path id="${u}" d="${d}"${def}/></defs>` +
    paints.map((p) => `<use href="#${u}"${p}/>`).join('')
  );
}

/** The same markup again, mirrored about the icon's vertical centre line. */
function mirrored(inner: string): string {
  const u = uid();
  return `<g id="${u}">${inner}</g><use href="#${u}" transform="matrix(-1 0 0 1 48 0)"/>`;
}
const SH_DN = ` transform="translate(${RO} ${RO})"`;
const SH_UP = ` transform="translate(-${RO} -${RO})"`;

/**
 * A RECESSED detail: a hole struck into the face and filled with enamel. The
 * face's chamfer around the hole is dark on the hole's top-left (it slopes
 * away from the light) and lit on its bottom-right.
 */
function recess(t: CrestTones, d: string, fill = cutFill()): string {
  return multi(d, '', [` fill="${t.lo}"${SH_UP}`, ` fill="${t.bevHi}"${SH_DN}`, ` fill="${fill}"`]);
}

/** A SMALL recess (a jewel, an eye): one flat fill. Its chamfer would be
 *  below a pixel on a phone and only turn into speckle. */
function inset(d: string, fill = cutFill()): string {
  return `<path d="${d}" fill="${fill}"/>`;
}

function recessCircle(t: CrestTones, cx: number, cy: number, r: number, fill = cutFill()): string {
  const c = (x: number, y: number, f: string) =>
    `<circle cx="${r2(x)}" cy="${r2(y)}" r="${r2(r)}" fill="${f}"/>`;
  if (r < 2.4) return c(cx, cy, fill);
  return c(cx - RO, cy - RO, t.lo) + c(cx + RO, cy + RO, t.bevHi) + c(cx, cy, fill);
}

/** An ENGRAVED line: a dark groove with a lit lower lip. Kept soft: on a
 *  1x screen a groove is under a pixel and must fade into the tone. */
function groove(t: CrestTones, d: string, w: number, opacity = 0.85): string {
  return multi(d, ' fill="none"', [
    ` stroke="${t.bevHi}" stroke-width="${r2(w * 0.9)}"${SH_DN} opacity="${r2(0.45 * opacity)}"`,
    ` stroke="${t.lo}" stroke-width="${r2(w)}" opacity="${r2(opacity)}"`,
  ]);
}

// ---- the icons ----------------------------------------------------------------

/**
 * Where the chrome horizon crosses an icon's face, in its own 48x48 box: the
 * horizon's height, the icon's top + bottom, the arc's radius (a round relief
 * gets a small one, so the horizon curves like a dome's; a flat piece gets a
 * near-straight one) and the reflection's contrast (1 sculpted ... ~.5 flat:
 * a flat piece reads its relief from the chamfer and side wall instead).
 * The horizon is placed on a real edge where the icon has one (the crown's
 * band, the car's shoulder line), so it never slices a shape in half.
 */
interface Horizon {
  y: number;
  top: number;
  bot: number;
  r: number;
  c: number;
  tilt?: number;
}

/**
 * One icon: its silhouette (attribute-free shapes; every pass paints them
 * through <use> with its own fill + stroke), an optional stroked part (poles,
 * handles; one width per icon), its details on the face, its chrome horizon,
 * extra defs, and the placement that gives every icon a similar optical size
 * and centre in the shield (scale `s` about the box centre, then shift).
 * `tile` is the icon's scale in the picker tile (wide icons pull in further
 * so nothing touches the tile's edge). `wall` scales the side wall's depth
 * (big flat silhouettes take a shallower one and let the soft shadow carry
 * the depth); a `dome` (the ball) has no side wall at all: a rounded relief
 * meets the enamel at its limb.
 */
interface IconArt {
  sil: string | ((id: string) => string);
  hz: Horizon;
  line?: { d: string; w: number };
  detail: (t: CrestTones, id: string) => string;
  defs?: (id: string, t: CrestTones) => string;
  s: number;
  dx: number;
  dy: number;
  tile?: number;
  wall?: number;
  dome?: boolean;
}

const P = (d: string) => `<path d="${d}"/>`;
const C = (cx: number, cy: number, r: number) =>
  `<circle cx="${r2(cx)}" cy="${r2(cy)}" r="${r2(r)}"/>`;
/** A silhouette half defined once and mirrored. */
const halves = (id: string, d: string) =>
  `<path id="${id}h" d="${d}"/><use href="#${id}h" transform="matrix(-1 0 0 1 48 0)"/>`;

// 0 SOCCER BALL: a classic ball. A centre pentagon, five partial pentagons
// round the rim (their seams about as long as a pentagon edge, so the panels
// between read as hexagons) and the seams between them, all struck into a
// sphere. The panels are bold so the default crest reads at any density.
const BALL_C = 24;
const BALL_R = 20.2;
const BALL_GEO = (() => {
  const centre = ngon(BALL_C, BALL_C, 6.3, 5, -90);
  const outer = centre.map((_, i) => {
    const a = -90 + i * 72;
    const rad = (a * Math.PI) / 180;
    const cx = BALL_C + 20.2 * Math.cos(rad);
    const cy = BALL_C + 20.2 * Math.sin(rad);
    // A vertex points back at the centre.
    return ngon(cx, cy, 7, 5, a + 180);
  });
  const seams: string[] = [];
  for (let i = 0; i < 5; i++) {
    // centre vertex to the outer pentagon's inward vertex
    seams.push(`M${pt(...centre[i])} L${pt(...outer[i][0])}`);
    // outer i (its vertex toward i+1) to outer i+1 (its vertex toward i)
    const a = outer[i][4];
    const b = outer[(i + 1) % 5][1];
    seams.push(`M${pt(...a)} L${pt(...b)}`);
  }
  return {
    centre: poly(centre),
    outer: outer.map((o) => poly(o)).join(' '),
    seams: seams.join(' '),
  };
})();

const soccerBall: IconArt = {
  sil: C(BALL_C, BALL_C, BALL_R),
  // a sphere's horizon is an arc, lowest under the middle
  hz: { y: 27, top: 3.8, bot: 44.2, r: 24, c: 1, tilt: 0 },
  defs: (id) =>
    `<clipPath id="${id}bc"><circle cx="${BALL_C}" cy="${BALL_C}" r="${BALL_R}"/></clipPath>` +
    // the sphere: a lit cap up-left, the limb rolling off into shadow down-right
    `<radialGradient id="${id}bs" gradientUnits="userSpaceOnUse" cx="18.5" cy="16" r="29">` +
    stop(0, WHITE, 0.2) +
    stop(0.4, WHITE, 0) +
    stop(0.66, BLACK, 0.06) +
    stop(0.86, BLACK, 0.3) +
    stop(1, BLACK, 0.62) +
    '</radialGradient>',
  detail: (t, id) =>
    `<g clip-path="url(#${id}bc)">` +
    groove(t, BALL_GEO.seams, 1, 0.7) +
    recess(t, BALL_GEO.centre, t.cut) +
    recess(t, BALL_GEO.outer, t.cut) +
    `<circle cx="${BALL_C}" cy="${BALL_C}" r="${BALL_R}" fill="url(#${id}bs)"/>` +
    '</g>' +
    `<ellipse cx="16.4" cy="12.8" rx="4.2" ry="2" transform="rotate(-34 16.4 12.8)" fill="${t.spec}" opacity=".6"/>`,
  s: 1,
  dx: 0,
  dy: 0,
};

// 1 CROWN: five points with jewelled tips over a jewelled band.
const crown: IconArt = {
  sil:
    P(
      'M7.5 34 L4 13.5 L13.6 23.2 L14.6 9.5 L20.4 20.6 L24 5.5 L27.6 20.6 L33.4 9.5 L34.4 23.2 L44 13.5 L40.5 34 Z',
    ) +
    P(
      'M7 33 H41 Q42.5 33 42.5 34.5 V41 Q42.5 42.5 41 42.5 H7 Q5.5 42.5 5.5 41 V34.5 Q5.5 33 7 33 Z',
    ) +
    C(4, 13.2, 2.7) +
    C(14.6, 9, 2.4) +
    C(24, 4.8, 2.9) +
    C(33.4, 9, 2.4) +
    C(44, 13.2, 2.7),
  // the horizon sits on the band's top edge: the points take the sky, the
  // band the ground
  hz: { y: 33.4, top: 2, bot: 42.5, r: 400, c: 0.5, tilt: 0.05 },
  detail: (t) =>
    // the band's seam + its jewels
    groove(t, 'M7 33.6 H41', 1.1) +
    recessCircle(t, 13, 37.8, 2.1) +
    recessCircle(t, 35, 37.8, 2.1) +
    recess(t, 'M24 34.6 L27.2 37.8 L24 41 L20.8 37.8 Z') +
    dPath('M24 35.9 L25.9 37.8 L24 39.7 L22.1 37.8 Z', t.hi, ' opacity=".9"') +
    // a gem in the centre of the crown
    inset('M24 22 L26.4 25.6 L24 29.2 L21.6 25.6 Z') +
    // facet highlights on the tips
    `<circle cx="23.1" cy="3.9" r=".95" fill="${t.spec}" opacity=".85"/>` +
    `<circle cx="3.2" cy="12.4" r=".75" fill="${t.spec}" opacity=".7"/>` +
    `<circle cx="13.8" cy="8.2" r=".75" fill="${t.spec}" opacity=".7"/>` +
    dLine('M8.4 31 L5.8 16.6', t.spec, 0.9, ' opacity=".6"'),
  s: 1,
  dx: 0,
  dy: 1,
};

// 2 LIGHTNING: a bold bolt with a lit left facet.
const lightning: IconArt = {
  sil: P('M27.4 2 L5.4 29 L19.2 29 L11.6 46.2 L42.6 16.8 L28.8 16.8 L38.4 2 Z'),
  hz: { y: 19.5, top: 2, bot: 46.2, r: 300, c: 0.55, tilt: 0.12 },
  detail: (t) =>
    dPath('M27.4 2 L5.4 29 L10.2 29 L29.4 5.2 L38.4 2 Z', t.spec, ' opacity=".42"') +
    dPath('M19.2 29 L11.6 46.2 L16.4 38.6 L22 29 Z', t.spec, ' opacity=".34"') +
    groove(t, 'M28.8 16.8 L42.6 16.8', 1, 0.6),
  s: 1.06,
  dx: 0,
  dy: 0,
};

// 3 FLAME: a layered flame: outer fire, an inner tongue, a hot core.
const flame: IconArt = {
  sil: P(
    'M24 46 C13.2 46 6.4 38.6 7 29 C7.4 22.2 11.6 17.6 13.6 11.6 C15.6 16 17 18.4 19.6 20 ' +
      'C18.6 12.8 21.8 6.2 27.6 2 C26.8 9 30.4 13.4 34.2 18 C36.4 15.4 37 12.4 36.4 8.6 ' +
      'C41.4 14 43.4 21.6 42.4 29.6 C41.2 39.4 34.2 46 24 46 Z',
  ),
  hz: { y: 19, top: 2, bot: 46, r: 70, c: 0.5, tilt: 0.1 },
  detail: (t, id) =>
    recess(
      t,
      'M24.2 43 C17.6 43 13.8 38.4 14.6 32.4 C15.2 27.8 18.4 25.4 19.8 21.4 C21.8 25 23.6 26.6 26 27.8 ' +
        'C25.4 23.4 26.8 19.6 29.6 16.8 C29.8 21.6 33.6 25.2 34.6 30.4 C35.8 37.4 31.6 43 24.2 43 Z',
    ) +
    dPath(
      'M24.4 40.6 C20.6 40.6 18.6 37.8 19.2 34.6 C19.8 31.8 21.8 30.4 22.6 27.8 C24.2 30 26 31 27.8 31.6 ' +
        'C28.6 30.2 28.8 28.8 28.6 27.4 C30.8 30 31.4 33 30.8 35.6 C30 38.8 27.6 40.6 24.4 40.6 Z',
      `url(#${id}i)`,
    ) +
    dLine('M10.6 30 C10.4 24.8 12.8 21 14.6 17', t.spec, 1, ' opacity=".5"'),
  s: 1,
  dx: 0,
  dy: 0,
};

// 4 TROPHY: a cup with handles, a stem and a stepped base.
const trophy: IconArt = {
  sil:
    P('M10.6 4.5 H37.4 V15 C37.4 24.6 31.8 30.4 24 31 C16.2 30.4 10.6 24.6 10.6 15 Z') +
    P('M21.4 30 H26.6 L27.4 36.6 H20.6 Z') +
    P('M15.4 36 H32.6 L34 40.6 H14 Z') +
    P(
      'M11.6 40.2 H36.4 Q37.6 40.2 37.6 41.4 V44.4 Q37.6 45.6 36.4 45.6 H11.6 Q10.4 45.6 10.4 44.4 V41.4 Q10.4 40.2 11.6 40.2 Z',
    ),
  line: {
    d:
      'M11.6 9.4 H7.6 C5.8 9.4 5 10.8 5 12.6 C5 18.4 8.6 21.8 13.6 22.8 ' +
      'M36.4 9.4 H40.4 C42.2 9.4 43 10.8 43 12.6 C43 18.4 39.4 21.8 34.4 22.8',
    w: 3.6,
  },
  hz: { y: 18.6, top: 4.5, bot: 45.6, r: 400, c: 0.5, tilt: 0.03 },
  detail: (t) =>
    groove(t, 'M10.8 8.6 H37.2', 1.2) +
    recess(t, star(24, 18.4, 6.2, 0.44)) +
    groove(t, 'M15 40.4 H33', 1) +
    dLine('M14.2 12 C14.2 19 16.8 24.4 21 27', t.spec, 1.4, ' opacity=".5"') +
    groove(t, 'M13.6 43 H34.4', 0.9, 0.6),
  s: 1,
  dx: 0,
  dy: 0,
};

// 5 WINGS: a pair of swept speed wings, four stepped feather tiers each,
// joined at a round boss with a star. The lowest tier sweeps UP into the boss.
const WING =
  'M22.8 19.2 C17.6 11.6 10.4 5 1 1 C4.6 4.4 7.6 6.8 11.2 8.6 ' +
  'C7.8 9 4.6 9 1.4 8.6 C5 12.4 8.4 14.6 12.4 15.8 C9.6 16.6 6.6 16.8 3.6 16.6 ' +
  'C7.2 20.2 10.8 22 14.6 22.4 C12.6 23.6 10.2 24.2 7.6 24.2 ' +
  'C11.6 28 16.8 30.2 23.4 30.4 Z';
const wings: IconArt = {
  sil: (id) => halves(id, WING) + C(24, 25.6, 7.2),
  hz: { y: 13.2, top: 1, bot: 32.8, r: 46, c: 0.55, tilt: 0 },
  detail: (t) => {
    const feathers =
      groove(t, 'M19.4 16.4 C15.4 12.6 11.8 10.4 8 9.2', 1.05) +
      groove(t, 'M18.6 20.8 C15.8 18.6 12.6 17.2 9.4 16.6', 1.05) +
      groove(t, 'M18.4 25 C16.2 24.2 14 23.4 11.8 23', 1.05) +
      dLine('M20.6 14.6 C15.8 9 10 5.4 4.2 3.2', t.spec, 0.9, ' opacity=".55"');
    return (
      mirrored(feathers) + recessCircle(t, 24, 25.6, 4.4) + dPath(star(24, 25.8, 3.1, 0.45), t.hi)
    );
  },
  s: 1.12,
  dx: 0,
  dy: 7,
  tile: 0.82,
};

// 6 CHECKERED FLAG: a waving flag with a real checker grid on a pole.
const FLAG = (() => {
  const x0 = 9.4;
  const x1 = 45;
  const top = (x: number) => {
    const u = (x - x0) / (x1 - x0);
    return 6.2 + 3 * Math.sin(u * Math.PI * 1.7 + 0.15) - u * 1.2;
  };
  const height = (x: number) => 23.2 - ((x - x0) / (x1 - x0)) * 2.2;
  const bot = (x: number) => top(x) + height(x);
  const at = (x: number, v: number) => top(x) + (bot(x) - top(x)) * v;
  const steps = 22;
  const topPts: [number, number][] = [];
  const botPts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const x = x0 + ((x1 - x0) * i) / steps;
    topPts.push([x, top(x)]);
    botPts.push([x, bot(x)]);
  }
  const outline = poly([...topPts, ...botPts.reverse()]);
  const cols = 5;
  const rows = 4;
  const cells: string[] = [];
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      if ((c + r) % 2 !== 0) continue;
      const xa = x0 + ((x1 - x0) * c) / cols;
      const xb = x0 + ((x1 - x0) * (c + 1)) / cols;
      const va = r / rows;
      const vb = (r + 1) / rows;
      const pts: [number, number][] = [];
      const sub = 4;
      for (let i = 0; i <= sub; i++) {
        const x = xa + ((xb - xa) * i) / sub;
        pts.push([x, at(x, va)]);
      }
      for (let i = sub; i >= 0; i--) {
        const x = xa + ((xb - xa) * i) / sub;
        pts.push([x, at(x, vb)]);
      }
      cells.push(poly(pts));
    }
  }
  // the folds: darker bands where the cloth turns away
  const fold = (xc: number, w: number) => {
    const pts: [number, number][] = [];
    for (let i = 0; i <= 6; i++) {
      const x = xc - w + (2 * w * i) / 6;
      pts.push([x, top(x)]);
    }
    for (let i = 6; i >= 0; i--) {
      const x = xc - w + (2 * w * i) / 6;
      pts.push([x, bot(x)]);
    }
    return poly(pts);
  };
  return { outline, cells: cells.join(' '), folds: `${fold(19.6, 3.4)} ${fold(38.4, 3.2)}` };
})();

const checkeredFlag: IconArt = {
  sil: (id) => C(7.6, 4.6, 2.7) + `<path id="${id}fo" d="${FLAG.outline}"/>`,
  line: { d: 'M7.6 45.4 V6', w: 3.2 },
  hz: { y: 17.4, top: 2, bot: 45.4, r: 300, c: 0.5, tilt: 0.1 },
  defs: (id) => `<clipPath id="${id}fc"><use href="#${id}fo"/></clipPath>`,
  detail: (t, id) =>
    `<g clip-path="url(#${id}fc)">` +
    recess(t, FLAG.cells, t.cut) +
    dPath(FLAG.folds, BLACK, ' opacity=".22"') +
    '</g>' +
    dLine('M6.6 9 V43', t.spec, 0.9, ' opacity=".55"') +
    `<circle cx="6.8" cy="3.8" r=".95" fill="${t.spec}" opacity=".8"/>`,
  s: 1,
  dx: 0,
  dy: 0,
};

// 7 RACE CAR: a low wedge sports car in side profile, facing right: a long
// falling hood, a low fastback cabin set well back, a rear wing and big
// wheels in deep arches. Scaled up past the others: a side profile is a
// wide, low shape, and at the others' scale it read as a small emblem.
const raceCar: IconArt = {
  sil:
    P(
      'M2.8 32.6 L2.6 27.6 C2.6 26.2 3.4 25.4 4.8 25.2 L13.4 24.2 ' +
        'C16.8 22.2 19.6 21.4 22.8 21.2 L29.8 21.2 C31.8 21.3 33.2 22 34.8 23.2 ' +
        'C39.6 24.2 43.6 25.8 46.2 27.6 C46.9 28.1 47.2 28.9 47 29.8 L46.6 31.8 ' +
        'C46.4 32.8 45.8 33.2 44.6 33.2 L42.8 33.2 A5.3 5.3 0 0 0 32.2 33.2 L17.4 33.2 ' +
        'A5.3 5.3 0 0 0 6.8 33.2 Z',
    ) +
    // rear wing: the blade + its stand
    P('M1.2 19.4 H11 L10.2 21.4 H7.6 L7 24.6 H5.4 L5.6 21.4 H1.8 Z') +
    C(12.1, 33.9, 4.7) +
    C(37.5, 33.9, 4.7),
  // the horizon runs along the shoulder line: the cabin takes the sky, the
  // flank the ground, like a real car's reflection
  hz: { y: 30, top: 19.4, bot: 38.6, r: 300, c: 0.5, tilt: 0.02 },
  detail: (t) =>
    // glasshouse + B-pillar
    recess(t, 'M15.2 24 C18.2 22.6 20.6 22.2 23 22.2 L29.6 22.2 C31.2 22.3 32.4 22.8 33.6 23.8 Z') +
    dLine('M27.4 22.3 L27 23.9', t.solid, 1) +
    // shoulder line, door shut line, side intake
    dLine('M6.4 27 C17 25.8 31 25.4 45.4 27.8', t.spec, 0.8, ' opacity=".7"') +
    groove(t, 'M25.8 24.2 L25.2 31.4', 0.8, 0.7) +
    recess(t, 'M18.6 28 L23.2 27.4 L21.6 30.8 L18.8 30.8 Z') +
    // lights
    dPath('M42 25.6 L46 27.4 L45.8 28.6 L41.6 27 Z', WHITE, ' opacity=".9"') +
    inset('M2.8 26.8 L5.6 26.2 L5.6 28.4 L2.8 28.8 Z') +
    // splitter
    dLine('M39.8 33 H46.2', t.lo, 0.9) +
    // wheels: rim + hub
    recessCircle(t, 12.1, 33.9, 2.6) +
    recessCircle(t, 37.5, 33.9, 2.6) +
    `<circle cx="12.1" cy="33.9" r="1" fill="${t.hi}"/>` +
    `<circle cx="37.5" cy="33.9" r="1" fill="${t.hi}"/>`,
  s: 1.18,
  dx: -0.2,
  dy: -5.4,
  tile: 0.79,
};

// 8 DRAGON: a dragon head in profile, facing right. Two long, slim horns
// swept back with a gap between them, a brow spike over a slit eye, a long
// snout with a nostril ridge, jaws open on a dark maw with fangs, and three
// spikes down the back of the neck.
const DRAGON_HEAD =
  // the back of the neck, three spikes up it
  'M11.6 46 L10.6 41.8 L4.6 40.6 L10.6 35.4 L5 32.4 L11.4 29.2 L6.8 25.6 L12.6 24.6 ' +
  'C13.2 23.4 13.8 22.4 14.6 21.6 ' +
  // the short horn, out and back
  'C10.8 19.8 6.6 17.8 2.2 16.4 C7.2 15.8 12.6 16.4 17.6 18.4 L19.4 17.2 ' +
  // the long horn, out and back
  'C14.4 12.4 8.6 7.8 3 3.6 C9.6 5.6 17 9.6 24.2 14.6 ' +
  // the skull, the brow spike, the snout over the nostril ridge
  'C25.6 14.4 26.8 14.6 28 15.2 L31.8 9.4 L33.4 16.6 ' +
  'C35.6 16.6 39.4 17 42.6 17.8 C44.6 18.2 46.4 19.4 46.6 21.2 ' +
  'C46.8 22.4 46.2 23.4 44.6 23.8 ' +
  // the upper lip back to the mouth corner, the lower jaw out to the chin
  'L28.4 27.4 L42.8 31.2 C43.8 31.6 43.8 32.8 42.6 33.2 ' +
  // under the jaw, the throat, down the front of the neck
  'C39.4 34.8 34 35.8 29 36.4 C25.4 37.2 23 40.6 22.6 46 Z';
const dragon: IconArt = {
  sil: P(DRAGON_HEAD),
  hz: { y: 18.8, top: 3.6, bot: 46, r: 120, c: 0.5, tilt: 0.12 },
  detail: (t) =>
    // the open maw (the jaws' notch) + fangs
    dPath('M28.9 27.4 L44.4 23.9 L42.6 31.1 Z', BLACK, ' opacity=".55"') +
    dPath('M32.6 26.7 L34.8 26.2 L33.6 29.1 Z', t.hi) +
    dPath('M39 25.2 L41 24.8 L40 27.6 Z', t.hi) +
    dPath('M35.4 29.6 L37.4 30.1 L36.8 27.4 Z', t.hi) +
    // horn ribs
    groove(t, 'M18.4 13.4 L20.6 11.8 M15 10.6 L17 9.2', 0.8, 0.7) +
    // slit eye under the brow ridge
    inset('M26.8 19.6 C28.6 17.8 31.4 17.6 33.4 18.6 C31.6 20.2 29 20.6 26.8 19.6 Z') +
    dPath('M30.2 18.2 C30.7 18.7 30.7 19.5 30.2 20 C29.7 19.5 29.7 18.7 30.2 18.2 Z', t.hi) +
    groove(t, 'M25.4 17.4 C28 16.4 30.8 16.4 33.6 17.4', 1) +
    // nostril
    inset('M41.8 19.4 C43 19 44.2 19.4 44.6 20.2 C43.4 20.6 42.4 20.4 41.8 19.4 Z') +
    // jaw hinge + throat scales
    groove(t, 'M25.4 25.2 C23.8 27.6 23.6 30.6 24.6 33.4', 1, 0.75) +
    groove(t, 'M16 28.4 C17.4 32.4 17.6 37 16.4 41.6', 0.9, 0.6) +
    // the lit brow + snout ridge
    dLine(
      'M24.4 15.2 C28 14.6 30 13 31.4 10.6 M34 17.4 C37.6 17.4 41 18 43.6 19',
      t.spec,
      0.8,
      ' opacity=".6"',
    ),
  s: 1,
  dx: 0,
  dy: 0,
};

// 9 LION: a lion's head from the front. A mirror-symmetric mane of twelve
// flame-like locks flowing out and down (short on top, long at the cheeks and
// in the beard), small round ears, a raised face plate with a heavy frowning
// brow, a broad nose on a long bridge, muzzle pads and a chin tuft.
const LION_C: [number, number] = [24, 23.4];
const LION_MANE = (() => {
  const [cx, cy] = LION_C;
  // angle measured clockwise from straight up
  const At = (r: number, deg: number): [number, number] => {
    const a = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const tipR = (deg: number) => {
    const a = (deg * Math.PI) / 180;
    return 19.4 + 3.2 * Math.sin(a) + 3.8 * Math.max(0, -Math.cos(a));
  };
  const step = 30;
  const half: string[] = [];
  const lines: string[] = [];
  for (let i = 0; i < 6; i++) {
    const a0 = i * step;
    const a1 = a0 + step;
    const tip = a0 + step * 0.7;
    const rt = tipR(tip);
    const r0 = rt * 0.7;
    const r1 = tipR(a1 + step * 0.7) * 0.7;
    const c1 = At(r0 * 1.22, a0 + step * 0.06);
    const c2 = At(rt * 0.97, tip - step * 0.42);
    const c3 = At(rt * 0.84, tip + step * 0.1);
    half.push(
      `C${pt(...c1)} ${pt(...c2)} ${pt(...At(rt, tip))} Q${pt(...c3)} ${pt(...At(i === 5 ? r0 : r1, a1))}`,
    );
    const la = At(r0 * 1.02, a0 + step * 0.36);
    const lc = At(rt * 0.8, tip - step * 0.32);
    const lb = At(rt * 0.9, tip - step * 0.08);
    lines.push(`M${pt(...la)} Q${pt(...lc)} ${pt(...lb)}`);
  }
  const start = At(tipR(step * 0.7) * 0.7, 0);
  return { right: `M${pt(...start)} ${half.join(' ')} Z`, locks: lines.join(' ') };
})();
const LION_FACE =
  'M24 13 C28.4 13 31.4 14 33 16.4 C34.6 18.8 35 21.6 34.6 24.2 C34.2 26.8 32.6 28.6 31.8 30.6 ' +
  'C31.2 34 28.2 37.4 24 37.6 C19.8 37.4 16.8 34 16.2 30.6 C15.4 28.6 13.8 26.8 13.4 24.2 ' +
  'C13 21.6 13.4 18.8 15 16.4 C16.6 14 19.6 13 24 13 Z';
const lion: IconArt = {
  // The mane is two mirrored halves plus a disc under them (so the seam down
  // the middle never shows a hairline).
  sil: (id) => C(LION_C[0], LION_C[1], 15.6) + halves(id, LION_MANE.right),
  // the mane's horizon arcs round the head
  hz: { y: 31, top: 1.5, bot: 47, r: 36, c: 0.65, tilt: 0 },
  // the face plate is a sculpted dome: lit up-left, rolling off down-right
  defs: (id, t) =>
    `<radialGradient id="${id}fd" gradientUnits="userSpaceOnUse" cx="20" cy="17.5" r="22">` +
    stop(0, mix(t.hi, t.spec, 0.4)) +
    stop(0.38, t.top) +
    stop(0.72, t.solid) +
    stop(1, t.shade) +
    '</radialGradient>',
  detail: (t, id) => {
    // behind the face: the mane's engraved lock lines and a round ear
    const back =
      groove(t, LION_MANE.locks, 1, 0.5) +
      dPath(
        'M28.4 14.4 C29 11.2 31.2 9.6 33.4 10 C35.4 10.6 35.8 13 34.6 16 Z',
        t.top,
        ` stroke="${t.lo}" stroke-width=".8" stroke-linejoin="round"`,
      ) +
      dLine('M30.6 13.6 C31.2 12.2 32.4 11.6 33.4 12', t.lo, 0.9);
    // on the face: a heavy brow angled down to the nose, a slanted eye, a
    // muzzle pad with whisker dots
    const front =
      recess(t, 'M25.6 21.6 C27.6 19.4 30.4 18.2 33.6 18 L33.2 19.8 C30.6 20 28.2 21 26.2 22.8 Z') +
      inset('M26.6 23.6 C28 22 30.2 21.6 31.8 22 C30.8 23.6 28.6 24.2 26.6 23.6 Z') +
      dPath(
        'M24 31 C26.4 30.6 29.8 31 30.4 33.2 C30.8 35 29 36.2 27 35.8 C25.4 35.6 24.4 34.6 24 33.4 Z',
        t.hi,
      ) +
      `<circle cx="26.6" cy="33" r=".45" fill="${t.lo}"/><circle cx="28.4" cy="33.4" r=".45" fill="${t.lo}"/>`;
    return (
      mirrored(back) +
      // the face plate: raised a step above the mane (a lit top-left lip, a
      // dark lower-right wall, a domed face)
      multi(LION_FACE, '', [
        ` fill="${t.deep}" transform="translate(.55 .65)"`,
        ` fill="${t.spec}" transform="translate(-.45 -.5)"`,
        ` fill="url(#${id}fd)"`,
      ]) +
      // the long, broad nose bridge
      dPath(
        'M22.2 20.4 C23.4 19.9 24.6 19.9 25.8 20.4 L27 26.4 L21 26.4 Z',
        t.hi,
        ' opacity=".55"',
      ) +
      mirrored(front) +
      // the broad nose: the face's hero feature
      recess(
        t,
        'M18.8 26.4 C21.4 25.2 26.6 25.2 29.2 26.4 C29 28.6 26.8 31 24 31.4 C21.2 31 19 28.6 18.8 26.4 Z',
      ) +
      dLine('M24 31.2 V33.6', t.cut, 1.1) +
      // chin + tuft
      dLine('M21.4 36.4 C23 37.2 25 37.2 26.6 36.4', t.lo, 0.9) +
      dPath('M21.2 37.2 C22.8 38 25.2 38 26.8 37.2 L24 42.8 Z', t.lo)
    );
  },
  s: 0.96,
  dx: 0,
  dy: 0,
};

const ICONS: readonly IconArt[] = [
  soccerBall,
  crown,
  lightning,
  flame,
  trophy,
  wings,
  checkeredFlag,
  raceCar,
  dragon,
  lion,
];

/** The relief (icon units, at scale 1): how deep the side wall runs down-
 *  right, and the lit chamfer's offset + width on the top-left edges. */
const WALL = 1.25;
const CHAMFER_OFF = 0.5;
const CHAMFER_W = 1;

/**
 * The face's chrome reflection: a radial gradient whose centre sits far
 * above the horizon (so its rings cross the icon as an arc, near-straight
 * for a big radius), the sky brightening into a thin streak, a hard break
 * into the dark band, then the ground recovering toward a floor bounce.
 */
function faceGradient(id: string, h: Horizon, t: CrestTones): string {
  const tilt = h.tilt ?? 0.1;
  const R = h.r;
  const cx = 24 - R * Math.sin(tilt);
  const cy = h.y - R * Math.cos(tilt);
  const rr = R + (h.bot - h.y) + 2;
  const at = (d: number) => +((R + d) / rr).toFixed(4);
  const up = h.top - h.y;
  const dn = h.bot - h.y;
  // A sculpted relief (c 1, the ball) keeps the rim's crisp break. A flatter
  // face is pillowed: its reflection breaks over a short feather, the dark
  // band is shallower and its ground sits nearer the metal's own tone, so
  // the face reads as one piece of metal, not two painted tones.
  const flat = 1 - h.c;
  const f = h.c >= 1 ? 0 : 1.6;
  const ground = mix(t.ground, t.solid, 0.7 * flat);
  const dark = mix(ground, t.dark, h.c);
  const streak = mix(t.sky1, t.streak, h.c);
  const stops: [number, string][] = [
    [up, t.sky0],
    [Math.min(up * 0.45, -2.6 - f), mix(t.sky0, t.sky1, 0.6)],
    [-1.3 - f * 0.6, t.sky1],
    [-0.75 - f * 0.5, streak],
    [-f * 0.5, streak],
    [f * 0.5, dark],
    [Math.max(dn * 0.2, f * 0.5 + 1.5), mix(dark, ground, 0.6)],
    [dn * 0.7, ground],
    [dn, t.floor],
  ];
  return (
    `<radialGradient id="${id}i" gradientUnits="userSpaceOnUse" cx="${r2(cx)}" cy="${r2(cy)}" r="${r2(rr)}">` +
    stops.map(([d, c]) => stop(at(d), c)).join('') +
    '</radialGradient>'
  );
}

/**
 * The defs an icon needs: its silhouette (painted by every pass), the face's
 * metal gradient, the recess fill, the two shadow blurs, and the per-icon
 * clips/shading.
 */
function iconDefs(id: string, art: IconArt, t: CrestTones): string {
  const line = art.line
    ? `<path id="${id}l" d="${art.line.d}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`
    : '';
  // The recess fill, per shape (objectBoundingBox): glossy enamel with the
  // shadow of the hole's top-left wall and a soft gloss, or a polished silver
  // inlay with its own horizon.
  const cut =
    t.cutKind === 'silver'
      ? `<linearGradient id="${id}ce" x1="0" y1="0" x2=".3" y2="1">` +
        stop(0, mix(t.cut, WHITE, 0.2)) +
        stop(0.44, mix(t.cut, WHITE, 0.55)) +
        stop(0.48, mix(t.cut, BLACK, 0.45)) +
        stop(0.74, mix(t.cut, BLACK, 0.18)) +
        stop(1, t.cut) +
        '</linearGradient>'
      : `<linearGradient id="${id}ce" x1="0" y1="0" x2=".45" y2="1">` +
        stop(0, mix(t.cut, BLACK, 0.36)) +
        stop(0.32, t.cut) +
        stop(0.62, mix(t.cut, WHITE, 0.1)) +
        stop(1, t.cut) +
        '</linearGradient>';
  return (
    `<g id="${id}s" stroke-linejoin="round">${typeof art.sil === 'string' ? art.sil : art.sil(id)}</g>` +
    line +
    faceGradient(id, art.hz, t) +
    cut +
    `<filter id="${id}sh" x="-25%" y="-25%" width="150%" height="150%"><feGaussianBlur stdDeviation="1.3"/></filter>` +
    `<filter id="${id}cs"><feGaussianBlur stdDeviation=".45"/></filter>` +
    (art.defs ? art.defs(id, t) : '')
  );
}

/** The icon's details (every stroked detail has round caps + joins). */
function details(art: IconArt, t: CrestTones, id: string): string {
  ctx.id = id;
  ctx.n = 0;
  return `<g stroke-linecap="round" stroke-linejoin="round">${art.detail(t, id)}</g>`;
}

/** One pass of the silhouette, `grow` fattened, in one paint. */
function silPass(id: string, art: IconArt, paint: string, grow: number, extra = ''): string {
  const g = grow ? ` stroke="${paint}" stroke-width="${r2(grow)}"` : '';
  let s = `<use href="#${id}s" fill="${paint}"${g}${extra}/>`;
  if (art.line) {
    s += `<use href="#${id}l" stroke="${paint}" stroke-width="${r2(art.line.w + grow)}"${extra}/>`;
  }
  return s;
}

const shift = (x: number, y: number) => ` transform="translate(${r2(x)} ${r2(y)})"`;

/** Every pass of one icon, in its own 48x48 space. `soft` is the soft cast
 *  shadow's strength. */
function iconLayers(icon: number, id: string, t: CrestTones, soft: number): string {
  const art = ICONS[icon] ?? ICONS[DEFAULT_CREST.icon];
  const { s } = art;
  // The relief keeps the same visible weight whatever the icon's scale.
  const k = 1 / s;
  const wall = art.dome ? 0 : WALL * (art.wall ?? 1) * k;
  const camOff = CHAMFER_OFF * t.bev * k;
  const place =
    s === 1 && art.dx === 0 && art.dy === 0
      ? ''
      : `translate(${r2(24 + art.dx)} ${r2(24 + art.dy)}) scale(${s}) translate(-24 -24)`;
  const sideWall = art.dome
    ? ''
    : // the side wall: the metal's thickness down-right, darker toward its
      // far edge, with a thin lit lip where it turns into the face
      silPass(id, art, t.wallFar, 0.7 * k, shift(wall, wall)) +
      silPass(id, art, t.wallNear, 0.5 * k, shift(wall * 0.5, wall * 0.5)) +
      silPass(id, art, t.lip, 0, shift(0.3 * k, 0.3 * k));
  const body =
    // the soft shadow it casts onto the enamel, and the tight contact shadow
    // at the foot of its side wall
    `<g${shift(1.6 * k, 2.4 * k)} filter="url(#${id}sh)" opacity="${soft}">` +
    silPass(id, art, BLACK, 1.2 * k) +
    '</g>' +
    `<g${shift(wall + 0.4 * k, wall + 0.6 * k)} filter="url(#${id}cs)" opacity=".55">` +
    silPass(id, art, BLACK, 0.5 * k) +
    '</g>' +
    // the keyline round the whole raised shape (face + side wall)
    `<g opacity="${t.edgeOp}">` +
    silPass(id, art, t.edge, t.edgeW * k) +
    (wall ? silPass(id, art, t.edge, t.edgeW * k, shift(wall, wall)) : '') +
    '</g>' +
    sideWall +
    // the lit chamfer on the top-left edges
    silPass(id, art, t.bevHi, CHAMFER_W * t.bev * k, shift(-camOff, -camOff)) +
    // the polished face + its details
    silPass(id, art, `url(#${id}i)`, 0) +
    details(art, t, id);
  return place ? `<g transform="${place}">${body}</g>` : body;
}

// ---- the shield ---------------------------------------------------------------

type P2 = [number, number];

/** One outline of the shield: apex, the top edge to the shoulder, the
 *  straight side, then a cubic down to the bottom tip (the right half; the
 *  left mirrors it). */
interface Outline {
  apex: P2;
  sh: P2;
  side: P2;
  c1: P2;
  c2: P2;
  tip: P2;
}

/** Outer rim edge, the bevel's crest, the field: the M140 shield. */
const OUT: Outline = {
  apex: [50, 3],
  sh: [95, 17],
  side: [95, 55],
  c1: [95, 84],
  c2: [75, 103],
  tip: [50, 113],
};
const MID: Outline = {
  apex: [50, 7.2],
  sh: [91, 19.9],
  side: [91, 55],
  c1: [91, 81.6],
  c2: [72.6, 99.2],
  tip: [50, 108.7],
};
const FLD: Outline = {
  apex: [50, 11],
  sh: [87, 22.6],
  side: [87, 55],
  c1: [87, 79],
  c2: [70, 95],
  tip: [50, 104],
};

const mx = (p: P2): P2 => [100 - p[0], p[1]];

function outlinePath(o: Outline): string {
  const [a, sh, sd, c1, c2, tp] = [o.apex, o.sh, o.side, o.c1, o.c2, o.tip];
  return (
    `M${pt(...a)} L${pt(...sh)} L${pt(...sd)} C${pt(...c1)} ${pt(...c2)} ${pt(...tp)} ` +
    `C${pt(...mx(c2))} ${pt(...mx(c1))} ${pt(...mx(sd))} L${pt(...mx(sh))} Z`
  );
}

const SHIELD_OUT = outlinePath(OUT);
const SHIELD_MID = outlinePath(MID);
const SHIELD_FIELD = outlinePath(FLD);

function bez(o: Outline, t: number): P2 {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return [
    a * o.side[0] + b * o.c1[0] + c * o.c2[0] + d * o.tip[0],
    a * o.side[1] + b * o.c1[1] + c * o.c2[1] + d * o.tip[1],
  ];
}

function bezTan(o: Outline, t: number): P2 {
  const u = 1 - t;
  return [
    3 * u * u * (o.c1[0] - o.side[0]) +
      6 * u * t * (o.c2[0] - o.c1[0]) +
      3 * t * t * (o.tip[0] - o.c2[0]),
    3 * u * u * (o.c1[1] - o.side[1]) +
      6 * u * t * (o.c2[1] - o.c1[1]) +
      3 * t * t * (o.tip[1] - o.c2[1]),
  ];
}

/** The outward normal of a right-half edge running down the shield. */
function outward(tx: number, ty: number): P2 {
  const n = Math.hypot(tx, ty) || 1;
  return [ty / n, -tx / n];
}

/** How far each facet leans off facing the viewer (radians). */
const TILT_OUT = 0.82;
const TILT_IN = 0.68;

/**
 * The rim's facets, shaded once at load. For each side: the two straight
 * facets (one flat brightness each: a flat facet reflects one direction) and
 * the curved facet as samples along its own gradient axis.
 */
interface Flat {
  d: string;
  b: number;
  inner: boolean;
}
interface Curve {
  d: string;
  axis: [P2, P2];
  stops: { at: number; b: number }[];
  inner: boolean;
}

const RIM = (() => {
  const flats: Flat[] = [];
  const curves: Curve[] = [];
  for (const side of [1, -1]) {
    const X = (p: P2): P2 => (side === 1 ? p : mx(p));
    for (const [outer, inner, tilt, face] of [
      [OUT, MID, TILT_OUT, 1],
      [MID, FLD, TILT_IN, -1],
    ] as const) {
      // the straight top edge + the straight side
      for (const [a, b] of [
        ['apex', 'sh'],
        ['sh', 'side'],
      ] as const) {
        const n = outward(outer[b][0] - outer[a][0], outer[b][1] - outer[a][1]);
        flats.push({
          d: poly([X(outer[a]), X(outer[b]), X(inner[b]), X(inner[a])]),
          b: envBrightness(side * n[0] * face, n[1] * face, tilt),
          inner: face === -1,
        });
      }
      // the curve: a band between the two cubics, shaded along an axis from
      // the side's foot to the tip
      const s0 = X(outer.side);
      const s1 = X(outer.tip);
      const ax = s1[0] - s0[0];
      const ay = s1[1] - s0[1];
      const len2 = ax * ax + ay * ay;
      const stops: { at: number; b: number }[] = [];
      const N = 40;
      for (let i = 0; i <= N; i++) {
        const t = i / N;
        const po = X(bez(outer, t));
        const pi = X(bez(inner, t));
        const mxp = (po[0] + pi[0]) / 2;
        const myp = (po[1] + pi[1]) / 2;
        const at = clamp(((mxp - s0[0]) * ax + (myp - s0[1]) * ay) / len2, 0, 1);
        const tg = bezTan(outer, Math.min(0.999, Math.max(0.001, t)));
        const n = outward(tg[0], tg[1]);
        stops.push({ at, b: envBrightness(side * n[0] * face, n[1] * face, tilt) });
      }
      const oc1 = X(outer.c1);
      const oc2 = X(outer.c2);
      const ic1 = X(inner.c1);
      const ic2 = X(inner.c2);
      curves.push({
        d:
          `M${pt(...s0)} C${pt(...oc1)} ${pt(...oc2)} ${pt(...s1)} L${pt(...X(inner.tip))} ` +
          `C${pt(...ic2)} ${pt(...ic1)} ${pt(...X(inner.side))} Z`,
        axis: [s0, s1],
        stops,
        inner: face === -1,
      });
    }
  }
  // The bottom-tip mitres: the two halves of each facet meet at the tip, one
  // facing up-left (lit), one up-right. Keep the seam but pull both sides
  // toward each other near the tip, so the mitre reads as one metal turning
  // a corner and not two materials butted together. (curves: right outer,
  // right inner, left outer, left inner.)
  for (const [a, b] of [
    [0, 2],
    [1, 3],
  ]) {
    const ca = curves[a].stops;
    const cb = curves[b].stops;
    const m = (ca[ca.length - 1].b + cb[cb.length - 1].b) / 2;
    for (const st of [...ca, ...cb]) {
      const u = clamp((st.at - 0.55) / 0.45, 0, 1);
      st.b += (m - st.b) * 0.7 * u * u * (3 - 2 * u);
    }
  }
  return { flats, curves };
})();

/** Drop gradient stops that a straight blend between their neighbours already
 *  reproduces (within `tol` per channel), so a smooth facet costs few stops. */
function simplifyStops(stops: { at: number; c: Rgb }[], tol = 5): { at: number; c: Rgb }[] {
  const keep = [stops[0]];
  let i = 0;
  while (i < stops.length - 1) {
    let j = i + 2;
    // extend the run while every middle stop sits on the straight blend
    for (; j < stops.length; j++) {
      const a = stops[i];
      const b = stops[j];
      let ok = true;
      for (let k = i + 1; k < j && ok; k++) {
        const m = stops[k];
        const f = b.at === a.at ? 0 : (m.at - a.at) / (b.at - a.at);
        for (let ch = 0; ch < 3; ch++) {
          if (Math.abs(a.c[ch] + (b.c[ch] - a.c[ch]) * f - m.c[ch]) > tol) ok = false;
        }
      }
      if (!ok) break;
    }
    i = j - 1;
    keep.push(stops[i]);
  }
  return keep;
}

/** The rim's facets in this crest's metal. The inner facet is capped below
 *  the full glint on a coloured metal: its brightest face reflects the
 *  metal's hue (pale gold), never a cream inlay. */
function rimSvg(id: string, ramp: Ramp, cap: number): string {
  let defs = '';
  let body = '';
  const at = (b: number, inner: boolean) => rampAt(ramp, inner ? Math.min(b, cap) : b);
  RIM.flats.forEach((f) => {
    body += `<path d="${f.d}" fill="${at(f.b, f.inner)}"/>`;
  });
  RIM.curves.forEach((cv, k) => {
    const stops = simplifyStops(cv.stops.map((s) => ({ at: s.at, c: toRgb(at(s.b, cv.inner)) })));
    defs +=
      `<linearGradient id="${id}c${k}" gradientUnits="userSpaceOnUse" x1="${r2(cv.axis[0][0])}" y1="${r2(cv.axis[0][1])}" x2="${r2(cv.axis[1][0])}" y2="${r2(cv.axis[1][1])}">` +
      stops.map((s) => stop(+s.at.toFixed(3), toHex(s.c))).join('') +
      '</linearGradient>';
    body += `<path d="${cv.d}" fill="url(#${id}c${k})"/>`;
  });
  return `<defs>${defs}</defs>${body}`;
}

/** The rim's chrome horizon: a vertical gradient in shield space over one
 *  facet ring, so both sides break at the same height. Above: the sky
 *  reflection brightening into a thin streak; at `y`: a hard break into the
 *  metal's own deep tone; below: the ground recovering. */
function horizonGrad(id: string, y: number, ramp: Ramp, sky: number, dark: number): string {
  const y0 = 14;
  const y1 = 84;
  const at = (v: number) => +((v - y0) / (y1 - y0)).toFixed(4);
  return (
    `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="0" y1="${y0}" x2="0" y2="${y1}">` +
    stop(0, ramp.spec, 0) +
    stop(at(y - 16), ramp.spec, sky * 0.35) +
    stop(at(y - 2.4), ramp.spec, sky) +
    stop(at(y - 1.2), ramp.spec, Math.min(0.92, sky * 2.2)) +
    stop(at(y), ramp.spec, Math.min(0.92, sky * 2.2)) +
    stop(at(y), ramp.deep, dark) +
    stop(at(y + 5), ramp.deep, dark * 0.6) +
    stop(at(y + 17), ramp.deep, 0) +
    '</linearGradient>'
  );
}

/** Where the 48x48 icon box lands in the shield: centred at (50, 55), just
 *  above the shield's middle (58), the ball about 64% of the field's width. */
const ICON_SCALE = 1.18;
const ICON_X = r2(50 - 24 * ICON_SCALE);
const ICON_Y = r2(55 - 24 * ICON_SCALE);

/** The sunburst guilloche under the enamel: one dashed circle whose stroke
 *  is as wide as its diameter, so every dash is a wedge from the centre.
 *  Fine (144 rays), faint, and masked to the field's middle band: it fades
 *  out toward the rim and dims behind the icon. */
const RAYS = 144;
const RAY_R = 30;
const RAY_DASH = r2(((Math.PI * RAY_R) / RAYS) * 1000) / 1000;

/** Where the rim's facets break into their dark horizon band (the inner
 *  facet a little lower, like a real bevel). */
const HZ_OUT = 52;
const HZ_IN = 59;

function clampIndex(v: number, n: number, fallback: number): number {
  return Number.isInteger(v) && v >= 0 && v < n ? v : fallback;
}

function clampCrest(c: Crest | null | undefined): Crest {
  const o = c ?? DEFAULT_CREST;
  return {
    icon: clampIndex(o.icon, CREST_ICONS, DEFAULT_CREST.icon),
    primary: clampIndex(o.primary, CLUB_COLORS.length, DEFAULT_CREST.primary),
    secondary: clampIndex(o.secondary, CLUB_COLORS.length, DEFAULT_CREST.secondary),
  };
}

/** A hex colour (#rgb or #rrggbb), or null. */
function validHex(v: string | undefined): string | null {
  return typeof v === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v) ? v : null;
}

/**
 * One club crest as an SVG string (viewBox 0 0 100 116). Out-of-range indices
 * fall back to DEFAULT_CREST's: a stale cache can never draw nothing.
 */
export function crestSvg(crest: Crest | null | undefined, opts: { title?: string } = {}): string {
  const c = clampCrest(crest);
  const id = `cr${++seq}`;
  const field = clubHex(c.primary);
  const metal = clubHex(c.secondary);
  const art = ICONS[c.icon];
  const t = crestTones(metal, field);
  const ramp = metalRamp(metal);
  const fieldLight = isLight(field);
  const fieldL = luminance(field);
  const sameish = contrast(metal, field) < SAMEISH_PAIR;
  const dk = ramp.dk;
  // A near-black rim on the app's near-black UI would vanish: its outer edge
  // is a lit line instead of a black one.
  const rimLine =
    dk > 0.5
      ? `stroke="${mix(ramp.hi, ramp.spec, 0.5)}" stroke-opacity=".7"`
      : `stroke="${BLACK}" stroke-opacity=".55"`;
  // The step from the rim down to the enamel: dark ink, unless both are dark
  // (then the lit lip of the metal, so a black-on-black crest keeps its rim).
  const step = !isLight(metal) && !fieldLight ? mix(ramp.hi, metal, 0.4) : mix(metal, BLACK, 0.75);
  // The enamel: deep and saturated top to bottom, deeper toward the bottom.
  // A same-colour pair sits deeper (dark) so the polished rim and icon lift
  // off it; a light enamel (white, sunburst, gold) stays bright, or it reads
  // as grey metal (a light same-colour icon steps its own face down instead).
  const lift = clamp((fieldL - 0.3) / 0.6, 0, 1);
  const mm = mul(field, field);
  const eTop = sameish ? mix(field, BLACK, 0.06 * (1 - lift)) : mix(field, WHITE, 0.05);
  const eMid = sameish
    ? mix(mix(field, mm, 0.5 * (1 - lift)), BLACK, 0.2 * (1 - lift) + 0.03 * lift)
    : field;
  const eLow = sameish
    ? mix(mix(field, mm, 1 - lift), BLACK, 0.6 * (1 - lift) + 0.16 * lift)
    : mix(field, BLACK, 0.48 - 0.3 * lift);
  // The glass: a faint reflection with one crisp, narrow streak on its edge.
  const veilA = fieldL > 0.5 ? 0.11 : 0.13;
  const streakA = fieldL > 0.5 ? 0.42 : 0.26;
  const rayInk = fieldLight ? BLACK : WHITE;
  const rayOp = fieldLight ? 0.05 : fieldL < 0.03 ? 0.065 : 0.055;
  // The inner facet's brightest: a coloured metal stops short of its glint.
  const cap = 1 - 0.16 * saturation(metal);
  const title = opts.title ? `<title>${escapeXml(opts.title)}</title>` : '';
  return (
    `<svg viewBox="0 0 100 116" xmlns="http://www.w3.org/2000/svg"${opts.title ? ' role="img"' : ' aria-hidden="true"'}>` +
    title +
    '<defs>' +
    `<filter id="${id}b1"><feGaussianBlur stdDeviation="1.1"/></filter>` +
    `<filter id="${id}b2"><feGaussianBlur stdDeviation="1.7"/></filter>` +
    // a long studio falloff across the flat facets
    `<linearGradient id="${id}rs" x1="0" y1="0" x2=".8" y2="1">` +
    stop(0, WHITE, 0.16) +
    stop(0.45, WHITE, 0) +
    stop(1, BLACK, 0.16) +
    '</linearGradient>' +
    // the bevel crest + the outer edge catch the light top-left (in the
    // metal's own glint colour)
    `<linearGradient id="${id}rl" x1="0" y1="0" x2=".7" y2="1">` +
    stop(0, ramp.spec, 0.95) +
    stop(0.42, ramp.spec, 0.35) +
    stop(0.75, ramp.spec, 0.06) +
    stop(1, ramp.spec, 0.2) +
    '</linearGradient>' +
    // the chrome horizon on both facets
    horizonGrad(`${id}ho`, HZ_OUT, ramp, 0.3 - 0.08 * dk, 0.7) +
    horizonGrad(`${id}hi`, HZ_IN, ramp, 0.16, 0.42) +
    // the enamel
    `<linearGradient id="${id}f" x1="0" y1="0" x2="0" y2="1">` +
    stop(0, eTop) +
    stop(0.5, eMid) +
    stop(1, eLow) +
    '</linearGradient>' +
    `<radialGradient id="${id}v" gradientUnits="userSpaceOnUse" cx="46" cy="46" r="62">` +
    stop(0.55, BLACK, 0) +
    stop(1, BLACK, r2(0.32 - 0.14 * lift)) +
    '</radialGradient>' +
    // the window reflection on the glossy enamel + its crisp streak
    `<linearGradient id="${id}g" gradientUnits="userSpaceOnUse" x1="0" y1="11" x2="0" y2="46">` +
    stop(0, WHITE, veilA) +
    stop(0.7, WHITE, veilA * 0.3) +
    stop(1, WHITE, veilA * 0.08) +
    '</linearGradient>' +
    `<linearGradient id="${id}gs" gradientUnits="userSpaceOnUse" x1="12" y1="0" x2="88" y2="0">` +
    stop(0, WHITE, 0) +
    stop(0.2, WHITE, streakA) +
    stop(0.55, WHITE, streakA * 0.5) +
    stop(1, WHITE, 0) +
    '</linearGradient>' +
    // the guilloche's mask: its middle band, dimmed behind the icon
    `<radialGradient id="${id}rm" gradientUnits="userSpaceOnUse" cx="50" cy="55" r="52">` +
    stop(0.22, WHITE, 0.3) +
    stop(0.48, WHITE, 1) +
    stop(0.72, WHITE, 0.75) +
    stop(0.98, WHITE, 0) +
    '</radialGradient>' +
    `<mask id="${id}m" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="116">` +
    `<rect width="100" height="116" fill="url(#${id}rm)"/></mask>` +
    // the one glint: a soft hot spot on the lit shoulder
    `<radialGradient id="${id}gl">` +
    stop(0, WHITE) +
    stop(0.3, ramp.spec, 0.6) +
    stop(1, ramp.spec, 0) +
    '</radialGradient>' +
    // the three shield outlines, defined once
    `<path id="${id}O" d="${SHIELD_OUT}"/><path id="${id}M" d="${SHIELD_MID}"/><path id="${id}F" d="${SHIELD_FIELD}"/>` +
    `<clipPath id="${id}k"><use href="#${id}F"/></clipPath>` +
    `<clipPath id="${id}kr"><use href="#${id}O"/></clipPath>` +
    iconDefs(id, art, t) +
    '</defs>' +
    // the badge's own contact shadow
    `<use href="#${id}O" transform="translate(0 1.6)" fill="${BLACK}" opacity=".55" filter="url(#${id}b1)"/>` +
    // THE RIM: an underlay (no seams between the facets), the facets, the
    // studio falloff, the chrome horizon, the lit crest of the bevel and the
    // outer edge
    `<use href="#${id}O" fill="${ramp.base}"/>` +
    rimSvg(id, ramp, cap) +
    `<use href="#${id}O" fill="url(#${id}rs)"/>` +
    `<path d="${SHIELD_OUT} ${SHIELD_MID}" fill-rule="evenodd" fill="url(#${id}ho)"/>` +
    `<path d="${SHIELD_MID} ${SHIELD_FIELD}" fill-rule="evenodd" fill="url(#${id}hi)"/>` +
    `<use href="#${id}M" fill="none" stroke="url(#${id}rl)" stroke-width=".55"/>` +
    `<use href="#${id}O" fill="none" ${rimLine} stroke-width="1"/>` +
    `<path d="M5.7 32 V17.5 L50 3.75 L94.3 17.5 V26" fill="none" stroke="url(#${id}rl)" stroke-width=".6"/>` +
    // THE ENAMEL
    `<use href="#${id}F" fill="url(#${id}f)"/>` +
    `<g clip-path="url(#${id}k)">` +
    `<use href="#${id}F" fill="url(#${id}v)"/>` +
    // the sunburst guilloche showing through
    `<circle cx="50" cy="55" r="${RAY_R}" fill="none" stroke="${rayInk}" stroke-width="${RAY_R * 2}" stroke-dasharray="${RAY_DASH} ${RAY_DASH}" opacity="${rayOp}" mask="url(#${id}m)"/>` +
    // the window reflection, and the crisp streak along its curved edge
    `<path d="M10 20 L50 9 L90 20 V36 C72 31 44 33 10 48 Z" fill="url(#${id}g)"/>` +
    `<path d="M10 48 C44 33 72 31 90 36 V34.4 C72 29.4 44 31.4 10 46.4 Z" fill="url(#${id}gs)"/>` +
    // the rim's shadow cast down-right onto the enamel
    `<path d="M-10 -10 H110 V126 H-10 Z ${SHIELD_FIELD}" fill-rule="evenodd" fill="${BLACK}" opacity=".62" transform="translate(.9 1.7)" filter="url(#${id}b2)"/>` +
    // the enamel's edge catching the light where it meets the metal
    `<use href="#${id}F" fill="none" stroke="${WHITE}" stroke-opacity="${fieldLight ? 0.35 : 0.2}" stroke-width="1.1" transform="translate(-.8 -1.1)"/>` +
    '</g>' +
    `<use href="#${id}F" fill="none" stroke="${step}" stroke-width="1"/>` +
    // THE ICON
    `<g transform="translate(${ICON_X} ${ICON_Y}) scale(${ICON_SCALE})">` +
    iconLayers(c.icon, id, t, 0.55) +
    '</g>' +
    // the glint: one soft hot spot on the lit top-left shoulder, short
    // arms, kept on the badge
    `<g clip-path="url(#${id}kr)">` +
    `<circle cx="7.6" cy="18.8" r="2.8" fill="url(#${id}gl)"/>` +
    `<path d="M7.6 15.6 L7.85 18.8 L7.6 22 L7.35 18.8 Z M4.4 18.8 L7.6 18.55 L10.8 18.8 L7.6 19.05 Z" fill="${WHITE}" opacity=".7"/>` +
    `<circle cx="50" cy="5.6" r="2.2" fill="url(#${id}gl)" opacity=".4"/>` +
    '</g>' +
    '</svg>'
  );
}

/** A crest element (the club hero, invites, the race chooser). */
export function crestEl(crest: Crest, className = 'crew-crest', title?: string): HTMLDivElement {
  const box = document.createElement('div');
  box.className = className;
  if (title) {
    box.setAttribute('role', 'img');
    box.setAttribute('aria-label', title);
  }
  box.innerHTML = crestSvg(crest);
  return box;
}

/**
 * The bare struck-metal icon (viewBox 0 0 48 48) in one metal, for the
 * picker's icon tiles. Pass the tile's field colour as `opts.fieldHex` so the
 * keyline and recessed details follow the same rule as the crest preview;
 * without it the tile is assumed to be the dark house colour.
 */
export function crestIconSvg(
  icon: number,
  colorHex: string,
  opts: { title?: string; fieldHex?: string } = {},
): string {
  const i = clampIndex(icon, CREST_ICONS, DEFAULT_CREST.icon);
  const color = validHex(colorHex) ?? clubHex(DEFAULT_CREST.secondary);
  const field = validHex(opts.fieldHex) ?? DEFAULT_BACKDROP;
  const id = `ci${++seq}`;
  const art = ICONS[i];
  const t = crestTones(color, field);
  const title = opts.title ? `<title>${escapeXml(opts.title)}</title>` : '';
  // pulled in so the chamfer, side wall and shadows never clip at the box
  // edge (wide icons pull in further)
  const k = art.tile ?? 0.87;
  return (
    `<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"${opts.title ? ' role="img"' : ' aria-hidden="true"'}>` +
    title +
    `<defs>${iconDefs(id, art, t)}</defs>` +
    `<g transform="translate(${r2(24 - 24 * k)} ${r2(22.8 - 24 * k)}) scale(${k})">` +
    iconLayers(i, id, t, 0.45) +
    '</g>' +
    '</svg>'
  );
}

/** A crest in words, e.g. 'LION · GOLD ON STEALTH' (icon colour ON field). */
export function crestLabel(c: Crest | null | undefined): string {
  const k = clampCrest(c);
  return `${CREST_ICON_NAMES[k.icon]} · ${clubColorName(k.secondary)} ON ${clubColorName(k.primary)}`;
}
